import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Organisation, CreateOrganisationDto, OrganisationError, OrganisationBasic, OrganisationResponse, OrganisationFullResponse, IRole } from './organisation.types';
import { BehaviorSubject, Observable, catchError, filter, map, of, switchMap, take, tap, throwError } from 'rxjs';
import { UserService } from '../user/user.service';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({ providedIn: 'root' })
export class OrganisationService {
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private _organisations = new BehaviorSubject<Organisation[] | null>(null);
    private _currentOrganisation = new BehaviorSubject<OrganisationBasic | null>(null);
    private _tempOrganisation: string | null = null;
    private _organisationMembers = new BehaviorSubject<any[] | null>(null);
    constructor(
        private _router: Router,
        private _snackBar: MatSnackBar
    ) {
        // Load organizations on service initialization
        this.loadOrganisations().subscribe();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    get organisations$(): Observable<Organisation[] | null> {
        // return only when after the LoadOrganisations is completed
        return this._organisations.asObservable().pipe(
            filter(organisations => organisations !== null)
        );
    }

    get organisationMembers$(): Observable<any[] | null> {
        return this._organisationMembers.asObservable();
    }

    get currentOrganisation$(): Observable<OrganisationBasic | Organisation | null> {
        return this._currentOrganisation.asObservable();
    }

    get currentOrganisation(): OrganisationBasic | null {
        return this._currentOrganisation.value;
    }

    get tempOrganisation(): string | null {
        return this._tempOrganisation;
    }

    set tempOrganisation(value: string | null) {
        this._tempOrganisation = value;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    //loadCurrentOrganisation
    /**
     * Load current organization from the list of organizations
     */
    loadCurrentOrganisation(): Observable<Organisation> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
        });
        return this._httpClient.get<{ data: OrganisationResponse }>(
            `${environment.apiUrlV1}organizations/` + this._currentOrganisation.value?.uuid,
            { headers }
        ).pipe(
            map(response => this.transformOrganisation(response.data)),
            tap(transformedOrg => {
                // Update organisations list if current organisation is not in the list
                this._currentOrganisation.next(transformedOrg);
                const currentOrgs = this._organisations.value || [];
                const orgIndex = currentOrgs.findIndex(org => org.uuid === transformedOrg.uuid);
                if (orgIndex > -1) {
                    currentOrgs[orgIndex] = transformedOrg;
                } else {
                    // If not found, add it to the list
                    currentOrgs.push(transformedOrg); 
                }
                this._organisations.next(currentOrgs);
            })
        );
    }


    /**
     * Load organizations
     */
    loadOrganisations(): Observable<Organisation[]> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
        });

        return this._httpClient.get<{ data: { data: OrganisationFullResponse[] } }>(
            `${environment.apiUrlV1}organizations`,
            { headers }
        ).pipe(
            map(response => response.data.data.map(org => this.transformOrganisation(org))),
            tap(transformedOrgs => {
                this._organisations.next(transformedOrgs);
                this.setCurrentOrganisation(transformedOrgs);
            })
        );
    }

    /**
     * Create a new organization
     */
    createOrganisation(data: CreateOrganisationDto): Observable<Organisation> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
        });

        return this._httpClient.post<{ data: { uuid: string } }>(
            `${environment.apiUrlV1}organizations`,
            data,
            { headers }
        ).pipe(
            switchMap(response => this._httpClient.get<{ data: OrganisationResponse }>(
                `${environment.apiUrlV1}organizations/${response.data.uuid}`,
                { headers }
            )),
            map(response => this.transformOrganisation(response.data)),
            tap(transformedOrg => {
                this._currentOrganisation.next(transformedOrg);
                this._organisations.next([...this._organisations.value, transformedOrg]);
            })
        );
    }

    /**
     * Transform organisation response to organisation
     */
    transformOrganisation(org: OrganisationResponse): Organisation {
        // check if media is an array
        // if it is, then filter out the media with type 'logo' and logo_small and extract the url of the media and assign it to logo and logoSmall
        // if it is not an array, then assign it to logo and logoSmall
        let logo = '';
        let logoSmall = '';
        if (org.media && org.media.length > 0) {
            logo = org.media.find(media => media.type === 'logo')?.base64_data || '';
            logoSmall = org.media.find(media => media.type === 'logo_small')?.base64_data || '';
        } 
        return {
            uuid: org.uuid,
            name: org.name,
            slug: org.slug,
            tradingName: org.trading_name,
            legalName: org.legal_name,
            ABN: org.abn_number,
            description: org.description,
            phone: org.phone,
            email: org.email,
            website: org.website,
            onboarded: org.onboard_status && org.onboard_status === 'completed',
            createdAt: new Date(org.created_at),
            updatedAt: new Date(org.updated_at),
            logo,  // Default empty string
            logoSmall,  // Default empty string
            initials: org.name.split(' ').map(name => name[0]).join('').substring(0, 2),  // break name by space and take first letter of each word but max of 2 letters
            services: [],  // Default empty array
            locations: [],  // Default empty array
            address: {
                street: org.address_line1,
                suburb: org.city,
                state: org.state as 'NSW' | 'VIC' | 'QLD' | 'SA' | 'WA' | 'TAS' | 'ACT' | 'NT',
                postcode: org.postcode,
            },
            media: org.media,
            hasSubscription: org.has_subscription || false,
            userUid: org.user_uid || ''
        };
    }

    /**
     * Update an existing organization
     */
    updateOrganisation(uuid: string, data: any, saveTempOrganisation: boolean = false): Observable<Organisation> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
        });

        return this._httpClient.put<{ success: boolean; message: string; data: OrganisationResponse }>(
            `${environment.apiUrlV1}organizations/${uuid}`,
            data,
            { headers }
        ).pipe(
            map(response => {
                if (response.success) {
                    this._snackBar.open('Organization updated successfully', 'Close', {
                        duration: 3000,
                        horizontalPosition: 'center',
                        verticalPosition: 'bottom',
                        panelClass: ['success-snackbar']
                    });
                    return this.transformOrganisation(response.data);
                } else {
                    throw new Error(response.message);
                }
            }),
            catchError(error => {
                this._snackBar.open(error.error?.message || 'Failed to update organization', 'Close', {
                    duration: 5000,
                    horizontalPosition: 'center',
                    verticalPosition: 'bottom',
                    panelClass: ['error-snackbar']
                });
                return throwError(() => error);
            }),
            tap(transformedOrg => {
                if (saveTempOrganisation) {
                    this._tempOrganisation = transformedOrg.slug;
                } else {
                    this._currentOrganisation.next(transformedOrg);
                    this._organisations.next(this._organisations.value.map(org => org.uuid === uuid ? transformedOrg : org));
                }
            })
        );
    }

    /**
     * Set current organization
     */
    setCurrentOrganisation(organisations: Organisation[] | OrganisationBasic[]): void {
        if (organisations.length === 0) {
            this._currentOrganisation.next(null);
            return;
        }

        if (organisations.length === 1) {
            // If only one organization, set it as current
            this._currentOrganisation.next(organisations[0]);
            return;
        }

        // If multiple organizations, check user preferences for default
        this._userService.user$.pipe(
            map(user => {
                // Get route params from router state
                const routerState = this._router.routerState.snapshot;
                const organisationSlug = routerState.root.firstChild?.params['organisationSlug'];
                
                if (organisationSlug) {
                    const org = organisations.find(org => org.slug === organisationSlug);
                    if (org) {
                        return org;
                    }
                }
                const defaultOrgSlug = user?.userPreferences?.find(pref => pref.key === 'defaultOrganisation')?.value;
                if (defaultOrgSlug) {
                    const defaultOrg = organisations.find(org => org.slug === defaultOrgSlug);
                    if (defaultOrg) {
                        return defaultOrg;
                    }
                }
                // If no default found, use the first organization
                return organisations[0];
            })
        ).subscribe(org => {
            this._currentOrganisation.next(org);
        });
    }

    /**
     * Switch current organization
     */
    switchOrganisation(organisation: OrganisationBasic, url: string | null = null): void {
        
        if (organisation) {
            this._currentOrganisation.next(organisation);
            this.getOrganisationMembers(organisation.uuid).subscribe(); 
            if (url) {
                this._router.navigate([organisation.slug + url]);
            } else {
                this._router.navigate([organisation.slug]);
            }
            
        }
    }

    /**
     * Clear organization data
     */
    clear(): void {
        this._organisations.next([]);
        this._currentOrganisation.next(null);
    }

    /**
     * Delete organisation
     */
    deleteOrganisation(organisation: Organisation): Observable<void> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
        });

        return this._httpClient.delete<void>(
            `${environment.apiUrlV1}organisations/${organisation.uuid}`,
            { headers }
        ).pipe(
            tap(() => {
                this._organisations.next(this._organisations.value.filter(org => org.uuid !== organisation.uuid));
            })
        );
    }

    /**
     * Check if a slug is available
     */
    checkSlugAvailability(slug: string): Observable<{ available: boolean }> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
        });

        return this._httpClient.post<{ available: boolean }>(
            `${environment.apiUrlV1}organizations/slug-availability`,
            { slug },
            { headers }
        ).pipe(
            map((res) => ({ available: res['data']?.is_available })),
            catchError((err) => throwError(() => err))
        );
    }

    getOrganisationBySlug(slug: string): Observable<Organisation> {
        const org = this._organisations.value.find(org => org.slug === slug);
        return of(org || null);
    }

    /**
     * Invite a team member
     */
    inviteTeamMember(organisationUuid: string, email: string, role: string): Observable<any> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
        });

        return this._httpClient.post(
            `${environment.apiUrlV1}organizations/${organisationUuid}/invitations`,
            {
                email,
                role: role.toUpperCase()
            },
            { headers }
        ).pipe(
            catchError(error => {
                if (error.status === 403) {
                    throw new Error('Maximum team seats reached');
                }
                throw new Error(error.error?.message || 'Failed to send invitation');
            })
        );
    }

    /**
     * Helper to convert snake_case to camelCase
     */
    private toCamelCase(obj: any): any {
        if (Array.isArray(obj)) {
            return obj.map(v => this.toCamelCase(v));
        } else if (obj !== null && obj.constructor === Object) {
            return Object.keys(obj).reduce((result, key) => {
                const camelKey = key.replace(/_([a-z])/g, g => g[1].toUpperCase());
                result[camelKey] = this.toCamelCase(obj[key]);
                return result;
            }, {} as any);
        }
        return obj;
    }

    /**
     * Get members of an organisation (camelCase)
     */
    getOrganisationMembers(orgUuid: string) {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
        });
        return this._httpClient.get<{ data: any[] }>(
            `${environment.apiUrlV1}organizations/${orgUuid}/members`,
            { headers }
        ).pipe(
            map(res => this.toCamelCase(res.data)),
            tap(res => {
                this._organisationMembers.next(res);
                this._userService.user$.pipe(take(1)).subscribe(user => {
                    this._userService.userRole = res.find(member => member.user.uid === user?.uid)?.role as IRole;
                });
            })
        );
    }

    /**
     * Update a member's role in an organisation
     */
    updateOrganisationMember(orgUuid: string, memberUuid: string, data: { role: string }) {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
        });
        return this._httpClient.put(
            `${environment.apiUrlV1}organizations/${orgUuid}/members/${memberUuid}`,
            data,
            { headers }
        );
    }

    /**
     * Delete a member from an organisation
     */
    deleteOrganisationMember(orgUuid: string, memberUuid: string) {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
        });
        return this._httpClient.delete(
            `${environment.apiUrlV1}organizations/${orgUuid}/members/${memberUuid}`,
            { headers }
        );
    }

    /**
     * Get invitations for an organisation (camelCase)
     */
    getOrganisationInvitations(orgUuid: string) {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
        });
        return this._httpClient.get<{ data: any[] }>(
            `${environment.apiUrlV1}organizations/${orgUuid}/invitations`,
            { headers }
        ).pipe(
            map(res => this.toCamelCase(res.data))
        );
    }

    /**
     * Delete an invitation
     */
    deleteOrganisationInvitation(orgUuid: string, inviteId: string) {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
        });
        return this._httpClient.delete(
            `${environment.apiUrlV1}organizations/${orgUuid}/invitations/${inviteId}`,
            { headers }
        );
    }

    /**
     * Resend an invitation
     */
    resendOrganisationInvitation(orgUuid: string, inviteId: string) {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
        });
        return this._httpClient.post(
            `${environment.apiUrlV1}organizations/${orgUuid}/invitations/${inviteId}/resend`,
            {},
            { headers }
        );
    }

    // {{base_url}}/api/v1/organizations/b8722e4c-3afb-4be2-8eb0-8951d77b2f17/media
    /**
     * Upload a logo for an organisation
     */
    uploadLogo(organisationUuid: string, file: File, type: string): Observable<any> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);

        const headers = new HttpHeaders({
            'X-Requested-With': 'XMLHttpRequest'
        });

        return this._httpClient.post(
            `${environment.apiUrlV1}organizations/${organisationUuid}/media`,
            formData,
            { headers }
        ).pipe(
            map(res => res),
            catchError((error: OrganisationError) => {
                // this._snackBar.open(error?.error?.message || 'Failed to upload logo', 'Close', {
                //     duration: 5000,
                //     horizontalPosition: 'center',
                //     verticalPosition: 'bottom',
                //     panelClass: ['error-snackbar']
                // });
                return throwError(() => error);
            })
        );

    }

    //{{base_url}}/api/v1/organizations/4e53c21d-4813-441a-b151-cd4f292471e1/media/90d7d4c8-45a9-4784-9823-0d8963e711f5
    /**
     * Delete a logo for an organisation
     */
    deleteLogo(organisationUuid: string, mediaUuid: string): Observable<any> {
        const headers = new HttpHeaders({
            'X-Requested-With': 'XMLHttpRequest'
        });

        return this._httpClient.delete(
            `${environment.apiUrlV1}organizations/${organisationUuid}/media/${mediaUuid}`,
            { headers }
        ).pipe(
            map(res => res),
            catchError((error: OrganisationError) => {
                // this._snackBar.open(error.error?.message || 'Failed to delete logo', 'Close', {
                //     duration: 5000,
                //     horizontalPosition: 'center',
                //     verticalPosition: 'bottom',
                //     panelClass: ['error-snackbar']
                // });
                return throwError(() => error);
            })
        );
    }
    // crate a method for check has subscribed or not 
    // currentOrg.hasSubscription || currentUser.uid === currentOrg.userUid
    hasSubscription(): boolean {
        const currentOrg = this._currentOrganisation.value;
        if (!currentOrg) {
            return true;
        }
        // console.log('Current Organisation:', currentOrg);
        let hasSubscription = false;
        this._userService.user$.pipe(take(1)).subscribe(currentUser => {
            hasSubscription = currentOrg ? (currentOrg.hasSubscription 
                // || currentUser?.uid === currentOrg.userUid
            ) : false;
        });
        return hasSubscription;
    }


    // is current user owner of the organisation
    isCurrentUserOwner(organisation: Organisation): boolean {
        return this._currentOrganisation.value?.userUid === this._userService.user?.uid;
    }

}
